/**
 * Management-status badge + menu for the Flow Designer toolbar.
 *
 * Shows a colored badge for the flow's manual lifecycle status (hidden when
 * `active`, the default/neutral state) and a menu to change it. Picking a
 * status opens a small dialog capturing an optional reason and optional
 * ticket references before submitting via `useSetWorkflowStatus`. Mirrors
 * `DegradedHealthMenu`'s icon-trigger-menu-then-dialog shape and
 * `DeactivateNodeDialog`'s reason-capture dialog.
 *
 * @module FlowDesigner/components/WorkflowStatusControl
 */
import React from 'react';
import {
  Button,
  Menu,
  MenuItem,
  MenuTrigger,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  TextArea,
  TextField,
  Select,
  TagBadge,
  Text,
  Flex,
  type TagColor,
  type SelectOption,
} from '@chatsdet/ui';
import {
  CaretDown,
  PauseCircle,
  ProhibitInset,
  Wrench,
  Archive,
  CheckCircle,
} from '@phosphor-icons/react';
import { WORKFLOW_MANAGEMENT_STATUSES, type WorkflowManagementStatus } from '@chatsdet/types';
import type { TicketInput } from '../hooks/useWorkflowEvents';

const STATUS_META: Record<
  WorkflowManagementStatus,
  { label: string; color: TagColor; icon: React.ReactNode }
> = {
  active: { label: 'Active', color: 'blue', icon: <CheckCircle size={12} weight="bold" /> },
  paused: { label: 'Paused', color: 'yellow', icon: <PauseCircle size={12} weight="bold" /> },
  blocked: { label: 'Blocked', color: 'red', icon: <ProhibitInset size={12} weight="bold" /> },
  under_construction: {
    label: 'Under construction',
    color: 'orange',
    icon: <Wrench size={12} weight="bold" />,
  },
  deprecated: { label: 'Deprecated', color: 'gray', icon: <Archive size={12} weight="bold" /> },
};

const STATUS_OPTIONS: SelectOption[] = WORKFLOW_MANAGEMENT_STATUSES.map((s) => ({
  id: s,
  label: STATUS_META[s].label,
}));

/** Parse a ticket entry as either an internal issue id (`iss_…`/uuid) or a URL. */
function parseTicketEntry(raw: string): TicketInput | null {
  const value = raw.trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return { url: value };
  if (/^iss_[a-z0-9]+$/i.test(value)) return { issueId: value };
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return { issueId: value };
  }
  return null;
}

function ticketLabel(ticket: TicketInput): string {
  return 'issueId' in ticket ? ticket.issueId : ticket.url;
}

type WorkflowStatusControlProps = {
  status: WorkflowManagementStatus;
  onSubmit: (input: { status: WorkflowManagementStatus; reason?: string; tickets?: TicketInput[] }) => void;
  isSubmitting?: boolean;
};

export function WorkflowStatusControl({ status, onSubmit, isSubmitting }: WorkflowStatusControlProps) {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [pendingStatus, setPendingStatus] = React.useState<WorkflowManagementStatus>(status);
  const [reason, setReason] = React.useState('');
  const [ticketDraft, setTicketDraft] = React.useState('');
  const [ticketError, setTicketError] = React.useState<string | null>(null);
  const [tickets, setTickets] = React.useState<TicketInput[]>([]);

  const openDialogFor = (next: WorkflowManagementStatus) => {
    setPendingStatus(next);
    setReason('');
    setTicketDraft('');
    setTicketError(null);
    setTickets([]);
    setIsDialogOpen(true);
  };

  const addTicket = () => {
    const parsed = parseTicketEntry(ticketDraft);
    if (!parsed) {
      setTicketError('Enter an issue id (iss_…) or a full URL');
      return;
    }
    setTickets((prev) => [...prev, parsed]);
    setTicketDraft('');
    setTicketError(null);
  };

  const meta = STATUS_META[status];

  return (
    <>
      <MenuTrigger>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          aria-label={`Flow status: ${meta.label}`}
        >
          {status !== 'active' && (
            <TagBadge name={meta.label} color={meta.color} icon={meta.icon} hideDot size="sm" />
          )}
          {status === 'active' && <Text size="sm">Active</Text>}
          <CaretDown size={12} />
        </Button>
        <Menu onAction={(key) => openDialogFor(key as WorkflowManagementStatus)}>
          {WORKFLOW_MANAGEMENT_STATUSES.map((s) => (
            <MenuItem key={s} id={s}>
              <Flex align="center" gap="sm">
                {STATUS_META[s].icon}
                <Text size="sm">{STATUS_META[s].label}</Text>
              </Flex>
            </MenuItem>
          ))}
        </Menu>
      </MenuTrigger>

      <DialogTrigger isOpen={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <span />
        <Dialog title="Change flow status">
          <DialogContent>
            <Flex direction="column" gap="md">
              <Select
                label="Status"
                items={STATUS_OPTIONS}
                selectedKey={pendingStatus}
                onSelectionChange={(key) => {
                  if (key) setPendingStatus(key as WorkflowManagementStatus);
                }}
              />
              <TextArea
                label="Reason (optional)"
                placeholder="Why are you making this change?"
                value={reason}
                onChange={setReason}
                rows={3}
              />
              <div className="space-y-2">
                <Flex gap="sm" align="end">
                  <TextField
                    label="Ticket (optional)"
                    placeholder="iss_… or https://…"
                    value={ticketDraft}
                    onChange={(v) => {
                      setTicketDraft(v);
                      if (ticketError) setTicketError(null);
                    }}
                    errorMessage={ticketError ?? undefined}
                    className="flex-1"
                  />
                  <Button variant="outline" size="sm" onPress={addTicket} isDisabled={!ticketDraft.trim()}>
                    Add
                  </Button>
                </Flex>
                {tickets.length > 0 && (
                  <Flex gap="xs" wrap>
                    {tickets.map((ticket, i) => (
                      <TagBadge
                        key={`${ticketLabel(ticket)}-${i}`}
                        name={ticketLabel(ticket)}
                        color="gray"
                        size="sm"
                        onRemove={() => setTickets((prev) => prev.filter((_, idx) => idx !== i))}
                      />
                    ))}
                  </Flex>
                )}
              </div>
            </Flex>
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={() => setIsDialogOpen(false)} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={isSubmitting}
              onPress={() => {
                const trimmedReason = reason.trim();
                onSubmit({
                  status: pendingStatus,
                  ...(trimmedReason ? { reason: trimmedReason } : {}),
                  ...(tickets.length > 0 ? { tickets } : {}),
                });
                setIsDialogOpen(false);
              }}
            >
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </>
  );
}
